import { X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { AUTH_CONTENT } from '../constants/authContent'
import type { InviteCodeUsedModalProps } from '../types'

const copy = AUTH_CONTENT.applyNow.step3.alreadyUsed

/**
 * Presentational modal for invite-code-already-used — matches the auth redesign.
 * Copy comes from AUTH_CONTENT; no hardcoded strings in JSX.
 */
export function InviteCodeUsedModal({
  isOpen,
  onClose,
  onSignIn,
}: InviteCodeUsedModalProps) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483640] flex items-center justify-center bg-black/40 p-4 backdrop-blur-[1px]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-code-used-title"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-xl border-l-4 border-red-500 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-2.5 px-4 pb-3 pt-4 sm:gap-3 sm:px-6 sm:pb-3 sm:pt-6">
          <span
            className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-red-400 bg-red-50 text-base font-bold leading-none text-red-500 sm:h-9 sm:w-9 sm:text-lg"
            aria-hidden="true"
          >
            !
          </span>
          <h2
            id="invite-code-used-title"
            className="min-w-0 flex-1 pr-8 text-base font-bold text-slate-900 sm:text-lg md:text-xl"
          >
            {copy.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 sm:right-4 sm:top-4"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 pb-4 sm:px-6">
          <div className="rounded-lg bg-red-50/80 px-3 py-3 text-sm leading-relaxed text-slate-800 sm:px-4 sm:text-[0.9375rem]">
            <p className="m-0">{copy.paragraph1}</p>
            <p className="mb-0 mt-3">{copy.paragraph2}</p>
          </div>
        </div>

        <div className="flex flex-col-reverse items-stretch justify-end gap-2.5 px-4 pb-4 sm:flex-row sm:items-center sm:gap-3 sm:px-6 sm:pb-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border-2 border-teal-dark bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-teal-dark transition-colors hover:bg-teal-50"
          >
            {copy.close}
          </button>
          <button
            type="button"
            onClick={onSignIn}
            className="rounded-md bg-teal-dark px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-teal-darker"
          >
            {copy.signIn}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
